import React, { useEffect, useState } from "react";
import "./App.css";

const LOGO = "/logo.png";

const OFFICES = [
  {
    city: "London",
    role: "United Kingdom",
    image:
      "https://images.unsplash.com/photo-1654292541857-9d5a6d8f3ef3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+44 20 7946 0110",
  },
  {
    city: "New York",
    role: "America",
    image:
      "https://images.unsplash.com/photo-1523540383849-4ae4125acd6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+1 212 555 0142",
  },
  {
    city: "Zurich",
    role: "Continental Europe",
    image:
      "https://images.unsplash.com/photo-1784490429133-a73b147fd554?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+41 44 668 0175",
  },
  {
    city: "Singapore",
    role: "Asia Pacific",
    image:
      "https://images.unsplash.com/photo-1496939376851-89342e90adcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+65 6812 0190",
  },
  {
    city: "Dubai",
    role: "Middle East",
    image:
      "https://images.unsplash.com/photo-1634007626524-f47fa37810a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+971 4 355 0128",
  },
  {
    city: "Cyprus",
    role: "Mediterranean",
    image:
      "https://images.unsplash.com/photo-1714484954991-c6ed8049f1fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    phone: "+357 25 090 0116",
  },
];

const DIVISIONS = [
  {
    no: "01",
    name: "Asset Management",
    body:
      "Through our Asset Management division, we deliver tailored investment strategies designed to protect and grow our clients' wealth across market cycles.",
    points: [
      "Discretionary & advisory portfolios",
      "Multi-asset investment strategies",
      "Long-term capital preservation",
    ],
  },
  {
    no: "02",
    name: "Commercial Brokerage",
    body:
      "Our Commercial Brokerage division connects clients with strategic opportunities across commercial markets, backed by the same rigour and relationship-driven approach that defines everything we do.",
    points: [
      "Commercial market access",
      "Strategic deal origination",
      "Relationship-led execution",
    ],
  },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "About", href: "#about" },
    { label: "Offices", href: "#offices" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <header
      className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}
      data-testid="main-navbar"
    >
      <div className="navbar__inner">
        <a href="#top" className="navbar__brand" data-testid="nav-logo">
          <img src={LOGO} alt="Hanbury Group" className="navbar__logo" />
        </a>

        <nav className="navbar__links" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="navbar__link"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          className="navbar__toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
        >
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
        </button>
      </div>

      <div
        className={`navbar__mobile ${open ? "navbar__mobile--open" : ""}`}
        data-testid="nav-mobile-menu"
      >
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            onClick={() => setOpen(false)}
            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
          >
            {l.label}
          </a>
        ))}
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top" data-testid="hero-section">
      <div className="hero__grid">
        <div className="hero__content reveal reveal--visible">
          <span className="eyebrow" data-testid="hero-eyebrow">
            01 &mdash; Global Financial Services
          </span>
          <h1 className="hero__title" data-testid="hero-title">
            A global institution with the responsiveness of a local partner.
          </h1>
          <div className="hero__lead">
            <p data-testid="hero-paragraph-1">
              We are a multinational financial services firm supporting clients
              around the world with international perspective, deep local
              knowledge, and an unwavering commitment to long-term
              relationships.
            </p>
          </div>
        </div>

        <aside className="hero__stats reveal reveal--visible" data-testid="hero-stats">
          {[
            { k: "6", v: "International offices" },
            { k: "2", v: "Specialist divisions" },
            { k: "Global", v: "Reach, local delivery" },
          ].map((s) => (
            <div className="stat" key={s.v}>
              <span className="stat__key">{s.k}</span>
              <span className="stat__val">{s.v}</span>
            </div>
          ))}
        </aside>
      </div>
    </section>
  );
}

function Divisions() {
  return (
    <section className="divisions" id="about" data-testid="divisions-section">
      <div className="section__head reveal">
        <div>
          <span className="eyebrow eyebrow--gold">02 &mdash; What We Do</span>
          <h2 className="section__title" data-testid="divisions-title">
            Two divisions, one standard.
          </h2>
        </div>
        <p className="section__intro" data-testid="divisions-intro">
          Two specialist practices, coordinated across jurisdictions &mdash;
          delivered with the precision and discretion of a single institution.
        </p>
      </div>

      <div className="divisions__body">
        <div className="divisions__media reveal">
          <img
            src="https://images.unsplash.com/photo-1600167990118-5ca20ea216fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
            alt="Hanbury Group global architecture"
            data-testid="divisions-image"
          />
        </div>

        <div className="divisions__grid">
          {DIVISIONS.map((d) => (
            <article
              className="division-card reveal"
              style={{ transitionDelay: `${d.no === "01" ? 0 : 120}ms` }}
              key={d.no}
              data-testid={`division-card-${d.no}`}
            >
              <span className="division-card__no">{d.no}</span>
              <h3 className="division-card__name">{d.name}</h3>
              <p className="division-card__body">{d.body}</p>
              <ul className="division-card__points">
                {d.points.map((p) => (
                  <li key={p}>
                    <i className="fa-solid fa-minus" />
                    {p}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Offices() {
  return (
    <section className="offices" id="offices" data-testid="offices-section">
      <div className="section__head reveal">
        <div>
          <span className="eyebrow eyebrow--gold">03 &mdash; Where We Operate</span>
          <h2 className="section__title" data-testid="offices-title">
            A presence across six markets.
          </h2>
        </div>
        <p className="section__intro" data-testid="offices-intro">
          From established financial capitals to the world&rsquo;s fastest
          growing hubs, our offices keep us close to every client we serve.
        </p>
      </div>

      <div className="offices__grid">
        {OFFICES.map((o, i) => (
          <article
            className="office-card reveal"
            style={{ transitionDelay: `${i * 90}ms` }}
            key={o.city}
            data-testid={`office-card-${o.city.toLowerCase().replace(" ", "-")}`}
          >
            <div className="office-card__img">
              <img src={o.image} alt={`${o.city} office`} loading="lazy" />
            </div>
            <div className="office-card__meta">
              {o.role && <span className="office-card__role">{o.role}</span>}
              <h3 className="office-card__city">{o.city}</h3>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="contact" id="contact" data-testid="contact-section">
      <div className="contact__inner">
        <div className="contact__head reveal">
          <span className="eyebrow eyebrow--gold">04 &mdash; Contact</span>
          <h2 className="contact__title" data-testid="contact-title">
            Speak with Hanbury Group.
          </h2>
          <p className="contact__preamble" data-testid="contact-preamble">
            For any questions, applications or queries about our services, send us an email to:
          </p>
          <a
            href="mailto:enquiries@hanbury-group.com"
            className="contact__email"
            data-testid="contact-email"
          >
            <i className="fa-regular fa-envelope" />
            enquiries@hanbury-group.com
          </a>
        </div>
      </div>

      <footer className="footer" data-testid="footer">
        <img src={LOGO} alt="Hanbury Group" className="footer__logo" />
        <span className="footer__copy">
          &copy; {new Date().getFullYear()} Hanbury Group. All rights reserved.
        </span>
      </footer>
    </section>
  );
}

export default function App() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="app" data-testid="app-root">
      <Navbar />
      <main>
        <Hero />
        <Divisions />
        <Offices />
        <Contact />
      </main>
    </div>
  );
}
